(function () {
  const MAIN_SELECTOR = '[data-product-main-media]';
  const MODAL_SELECTOR = 'product-image-zoom-modal';
  const ZOOM_IMG_CLASS = 'image-magnify-hover';
  const DELEGATED_ATTR = 'data-zoom-delegated';
  const MOBILE_QUERY = '(max-width: 767px)';

  function isMobile() {
    return window.matchMedia(MOBILE_QUERY).matches;
  }

  function getImageSrc(img) {
    return (
      img.dataset.original ||
      img.currentSrc ||
      img.src ||
      img.dataset.src ||
      ''
    );
  }

  function getPointerEvent(event) {
    return event.touches?.[0] || event.changedTouches?.[0] || event;
  }

  function removeOverlays(container) {
    container?.querySelectorAll('.image-magnify-full-size').forEach((el) => el.remove());
  }

  function moveWithHover(img, event, scale, overlay) {
    const pointer = getPointerEvent(event);
    const imgWidth = img.width || img.clientWidth || img.naturalWidth || 1;
    const imgHeight = img.height || img.clientHeight || img.naturalHeight || imgWidth;
    const ratio = imgHeight / imgWidth;
    const rect = img.getBoundingClientRect();
    const x = pointer.clientX - rect.left;
    const y = pointer.clientY - rect.top;
    const posX = (x / (img.clientWidth / 100)) + '%';
    const posY = (y / ((img.clientWidth * ratio) / 100)) + '%';

    overlay.style.backgroundPosition = `${posX} ${posY}`;
    overlay.style.backgroundSize = `${imgWidth * scale}px`;
  }

  function activateMagnify(img, event, scale, container) {
    removeOverlays(container);

    const wrapper = img.closest('.image-wrapper') || img.parentElement;
    if (!wrapper) return;

    wrapper.style.position = 'relative';

    const overlay = document.createElement('div');
    overlay.className = 'image-magnify-full-size';
    overlay.setAttribute('aria-hidden', 'true');
    overlay.style.backgroundColor = 'var(--gradient-background, #fff)';

    const preload = new Image();
    const previousOpacity = img.style.opacity;

    img.style.opacity = '0.5';
    preload.src = getImageSrc(img);

    const showOverlay = () => {
      img.style.opacity = '100%';
      overlay.style.backgroundImage = `url('${preload.src}')`;
      wrapper.insertBefore(overlay, img.nextSibling);
      moveWithHover(img, event, scale, overlay);

      const updatePosition = (ev) => moveWithHover(img, ev, scale, overlay);

      overlay.addEventListener('mousemove', updatePosition);
      overlay.addEventListener('touchmove', updatePosition, { passive: true });
      overlay.addEventListener('click', () => overlay.remove());
      overlay.addEventListener('mouseleave', () => overlay.remove());
    };

    if (preload.complete) {
      showOverlay();
    } else {
      preload.onload = showOverlay;
      preload.onerror = () => {
        img.style.opacity = previousOpacity || '100%';
      };
    }
  }

  function getSlides(modal) {
    return Array.from(modal.querySelectorAll('[data-zoom-modal-slide]'));
  }

  function getSlideIndex(slides, mediaId) {
    return slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
  }

  function getModalTrack(modal) {
    return modal.querySelector('[data-zoom-modal-track]');
  }

  function getActiveIndexFromScroll(track, slides) {
    if (!track || !slides.length) return 0;

    const trackCenter = track.getBoundingClientRect().left + track.clientWidth / 2;
    let activeIndex = 0;
    let minDistance = Infinity;

    slides.forEach((slide, index) => {
      const rect = slide.getBoundingClientRect();
      const slideCenter = rect.left + rect.width / 2;
      const distance = Math.abs(slideCenter - trackCenter);

      if (distance < minDistance) {
        minDistance = distance;
        activeIndex = index;
      }
    });

    return activeIndex;
  }

  function getVisibleSlide(modal) {
    const track = getModalTrack(modal);
    const slides = getSlides(modal);

    if (isMobile() && track) {
      return slides[getActiveIndexFromScroll(track, slides)];
    }

    return modal.querySelector('.product-image-zoom-modal__slide.is-active');
  }

  function scrollToSlide(modal, index, smooth) {
    const track = getModalTrack(modal);
    if (!track) return;

    track.scrollTo({
      left: index * track.clientWidth,
      behavior: smooth ? 'smooth' : 'auto',
    });
  }

  function updateSlideUI(modal, activeIndex, slides) {
    slides.forEach((slide, i) => {
      slide.classList.toggle('is-active', i === activeIndex);
    });

    modal.querySelectorAll('[data-zoom-modal-thumb]').forEach((thumb, i) => {
      thumb.classList.toggle('is-active', i === activeIndex);
    });

    const prevBtn = modal.querySelector('[data-zoom-modal-prev]');
    const nextBtn = modal.querySelector('[data-zoom-modal-next]');
    const counter = modal.querySelector('[data-zoom-modal-counter]');

    if (prevBtn) prevBtn.disabled = activeIndex <= 0;
    if (nextBtn) nextBtn.disabled = activeIndex >= slides.length - 1;

    modal.dataset.activeMediaId = slides[activeIndex]?.dataset.mediaId || '';

    if (counter) {
      counter.textContent = `${activeIndex + 1} / ${slides.length}`;
    }

    const activeThumb = modal.querySelector('[data-zoom-modal-thumb].is-active');
    activeThumb?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
  }

  function setActiveSlide(modal, mediaId, smooth) {
    const slides = getSlides(modal);
    const index = getSlideIndex(slides, mediaId);
    const activeIndex = index >= 0 ? index : 0;

    removeOverlays(modal);

    if (isMobile()) {
      scrollToSlide(modal, activeIndex, smooth !== false);
    }

    updateSlideUI(modal, activeIndex, slides);
  }

  function setActiveSlideByIndex(modal, index, smooth) {
    const slides = getSlides(modal);
    if (index < 0 || index >= slides.length) return;

    removeOverlays(modal);

    if (isMobile()) {
      scrollToSlide(modal, index, smooth !== false);
    }

    updateSlideUI(modal, index, slides);
  }

  function openModal(modal, mediaId) {
    modal.hidden = false;
    modal.setAttribute('aria-hidden', 'false');
    document.body.classList.add('no-scroll');
    setActiveSlide(modal, mediaId, false);

    if (isMobile()) {
      requestAnimationFrame(() => {
        const slides = getSlides(modal);
        const index = getSlideIndex(slides, mediaId);
        scrollToSlide(modal, index >= 0 ? index : 0, false);
      });
    }
  }

  function closeModal(modal) {
    modal.hidden = true;
    modal.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('no-scroll');
    removeOverlays(modal);
  }

  function navigateModal(modal, direction) {
    const slides = getSlides(modal);
    const currentIndex = getSlideIndex(slides, modal.dataset.activeMediaId);
    const nextIndex = currentIndex + direction;

    if (nextIndex < 0 || nextIndex >= slides.length) return;
    setActiveSlideByIndex(modal, nextIndex, true);
  }

  function findModal(section, settingsEl) {
    return (
      section.querySelector(MODAL_SELECTOR) ||
      document.getElementById(`ProductImageZoomModal-${settingsEl.dataset.sectionId}`)
    );
  }

  function getSlideFromElement(element) {
    return element?.closest?.('[data-product-zoom-slide]');
  }

  function bindModalSlider(modal) {
    const track = getModalTrack(modal);
    if (!track || track.getAttribute('data-slider-bound') === 'true') return;

    track.setAttribute('data-slider-bound', 'true');

    let scrollTimer;

    track.addEventListener('scroll', () => {
      if (!isMobile() || modal.hidden) return;
      if (track.querySelector('.image-magnify-full-size')) return;

      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(() => {
        const slides = getSlides(modal);
        const activeIndex = getActiveIndexFromScroll(track, slides);
        updateSlideUI(modal, activeIndex, slides);
      }, 60);
    }, { passive: true });
  }

  function bindModalMagnify(modal, settings) {
    if (!modal || settings.enableLens !== 'true') return;
    if (modal.getAttribute('data-modal-magnify-bound') === 'true') return;

    modal.setAttribute('data-modal-magnify-bound', 'true');

    modal.addEventListener('click', (event) => {
      if (modal.hidden) return;
      if (event.target.closest('[data-zoom-modal-close], [data-zoom-modal-prev], [data-zoom-modal-next], [data-zoom-modal-thumb]')) {
        return;
      }
      if (event.target.classList.contains('image-magnify-full-size')) return;

      const activeSlide = getVisibleSlide(modal);
      if (!activeSlide || !activeSlide.contains(event.target)) return;

      const img = activeSlide.querySelector(`.${ZOOM_IMG_CLASS}`);
      if (!img) return;

      event.preventDefault();
      event.stopPropagation();
      activateMagnify(img, event, settings.magnifyScale, modal);
    });
  }

  function bindModalControls(modal, settings) {
    if (!modal || modal.getAttribute('data-modal-bound') === 'true') return;

    modal.setAttribute('data-modal-bound', 'true');
    bindModalSlider(modal);
    bindModalMagnify(modal, settings);

    modal.querySelector('[data-zoom-modal-close]')?.addEventListener('click', () => closeModal(modal));
    modal.querySelector('[data-zoom-modal-prev]')?.addEventListener('click', () => navigateModal(modal, -1));
    modal.querySelector('[data-zoom-modal-next]')?.addEventListener('click', () => navigateModal(modal, 1));

    modal.querySelectorAll('[data-zoom-modal-thumb]').forEach((thumb) => {
      thumb.addEventListener('click', () => setActiveSlide(modal, thumb.dataset.mediaId, true));
    });

    document.addEventListener('keydown', (event) => {
      if (modal.hidden) return;
      if (event.key === 'Escape') closeModal(modal);
      if (event.key === 'ArrowLeft') navigateModal(modal, -1);
      if (event.key === 'ArrowRight') navigateModal(modal, 1);
    });
  }

  function bindFlickityMobileTap(mainMedia, modal, settings) {
    if (!modal || settings.enableMobileTap !== 'true') return;

    const attachStaticClick = () => {
      if (mainMedia.getAttribute('data-flickity-zoom-bound') === 'true') return;

      const flkty = window.Flickity?.data(mainMedia);
      if (!flkty) return;

      mainMedia.setAttribute('data-flickity-zoom-bound', 'true');

      flkty.on('staticClick', (_event, _pointer, cellElement) => {
        if (!isMobile()) return;
        if (_event.target?.closest?.('[data-product-zoom-open]')) return;

        const slide =
          cellElement?.matches?.('[data-product-zoom-slide]')
            ? cellElement
            : cellElement?.querySelector?.('[data-product-zoom-slide]');

        if (!slide) return;
        openModal(modal, slide.dataset.mediaId);
      });
    };

    if (window.Flickity?.data(mainMedia)) {
      attachStaticClick();
    }

    if (typeof jQuery !== 'undefined') {
      jQuery(mainMedia).on('ready.flickity', attachStaticClick);
    }

    mainMedia.addEventListener('ready.flickity', attachStaticClick);
    setTimeout(attachStaticClick, 800);
    setTimeout(attachStaticClick, 2000);
  }

  function bindZoomInteractions(section, settings) {
    const mainMedia = section.querySelector(MAIN_SELECTOR);
    const modal = findModal(section, settings.settingsEl);

    if (!mainMedia || mainMedia.getAttribute(DELEGATED_ATTR) === 'true') return;

    mainMedia.setAttribute(DELEGATED_ATTR, 'true');

    if (modal) {
      bindModalControls(modal, settings);
      bindFlickityMobileTap(mainMedia, modal, settings);
    }

    mainMedia.addEventListener('click', (event) => {
      if (settings.enableLens !== 'true') return;

      const openBtn = event.target.closest('[data-product-zoom-open]');
      if (openBtn && modal) {
        event.preventDefault();
        event.stopPropagation();
        openModal(modal, openBtn.dataset.mediaId);
        return;
      }

      if (event.target.classList.contains('image-magnify-full-size')) return;

      const slide = getSlideFromElement(event.target);
      if (!slide || !mainMedia.contains(slide)) return;

      if (isMobile() && modal && settings.enableMobileTap === 'true') {
        if (mainMedia.classList.contains('flickity-enabled')) return;

        event.preventDefault();
        event.stopPropagation();
        openModal(modal, slide.dataset.mediaId);
        return;
      }

      if (isMobile()) return;

      const img = event.target.closest(`.${ZOOM_IMG_CLASS}`);
      if (!img) return;

      event.preventDefault();
      event.stopPropagation();
      activateMagnify(img, event, settings.magnifyScale, mainMedia);
    });
  }

  function initSection(section) {
    const settingsEl = section.querySelector('[data-product-zoom-settings]');
    if (!settingsEl || settingsEl.dataset.enabled !== 'true') return;

    bindZoomInteractions(section, {
      settingsEl,
      enableLens: settingsEl.dataset.enableLens,
      enableMobileTap: settingsEl.dataset.enableMobileTap,
      magnifyScale: parseFloat(settingsEl.dataset.magnifyScale || '2'),
    });
  }

  function initAll() {
    document.querySelectorAll('.main-product-section').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('product-info:loaded', (event) => {
    const section = event.target?.closest?.('.main-product-section');
    if (section) initSection(section);
  });
})();
